import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, Building2, Image as ImageIcon, X as XIcon } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const MAX_PHOTOS = 5;

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "",
  name: "",
  description: "",
  photos: [],
  order: 0,
  status: "Active",
};

function Field({ label, children, hint, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-[#6b655b] mt-1.5 leading-snug">{hint}</div>}
    </label>
  );
}

export default function Facilities() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/facilities");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openAdd = () => {
    setForm({ ...emptyForm, order: items.length });
    setOpen(true);
  };

  const openEdit = (f) => {
    setForm({ ...emptyForm, ...f, photos: Array.isArray(f.photos) ? [...f.photos] : [] });
    setOpen(true);
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const setPhotoAt = (idx, next) => {
    setForm((s) => {
      const photos = [...(s.photos || [])];
      photos[idx] = { url: next.url || "", public_id: next.public_id || "" };
      return { ...s, photos };
    });
  };

  const removePhotoAt = (idx) => {
    setForm((s) => {
      const photos = [...(s.photos || [])];
      photos.splice(idx, 1);
      return { ...s, photos };
    });
  };

  const addPhotoSlot = () => {
    setForm((s) => {
      const photos = [...(s.photos || [])];
      if (photos.length >= MAX_PHOTOS) return s;
      photos.push({ url: "", public_id: "" });
      return { ...s, photos };
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Facility name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: (form.description || "").trim(),
      photos: (form.photos || []).filter((p) => p && p.url),
      order: Number(form.order) || 0,
      status: form.status === "Inactive" ? "Inactive" : "Active",
    };
    if (payload.photos.length > MAX_PHOTOS) {
      toast.error(`At most ${MAX_PHOTOS} photos allowed`);
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/institute/facilities/${form.id}`, payload);
        toast.success("Facility updated");
      } else {
        await api.post("/institute/facilities", payload);
        toast.success("Facility added");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/facilities/${confirmDelete.id}`);
      toast.success("Facility deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="i-facilities-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Institute CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Campus Facilities</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">
            Add facility cards (name, description and up to {MAX_PHOTOS} photos · 300 KB each) shown on the public website.
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={openAdd}
            data-testid="i-facilities-add-btn"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-colors"
          >
            <Plus size={16} strokeWidth={1.8} /> Add Facility
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]" data-testid="i-facilities-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div
          data-testid="i-facilities-empty"
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm py-16 text-center"
        >
          <Building2 size={36} strokeWidth={1.2} className="mx-auto text-[#bcb6aa] mb-3" />
          <div className="text-[#1c1a17] font-medium">No facilities yet</div>
          <div className="text-sm text-[#6b655b] mt-1">Click <em>Add Facility</em> to create one.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="i-facilities-grid">
          {items.map((f) => (
            <div
              key={f.id}
              className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col"
              data-testid={`i-facilities-card-${f.id}`}
            >
              <div className="aspect-[16/9] bg-[#f5f1e8] overflow-hidden relative">
                {f.photos?.[0]?.url ? (
                  <img src={f.photos[0].url} alt={f.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#bcb6aa]">
                    <ImageIcon size={32} strokeWidth={1.2} />
                  </div>
                )}
                {(f.photos?.length || 0) > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/65 text-white text-[11px] px-2 py-0.5 rounded-sm">
                    +{f.photos.length - 1} more
                  </div>
                )}
                {f.status !== "Active" && (
                  <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">
                    Inactive
                  </div>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-semibold text-[#1c1a17] leading-snug">{f.name}</div>
                {f.description && (
                  <div className="text-sm text-[#5b5650] mt-1.5 line-clamp-3">{f.description}</div>
                )}
                {canEdit && (
                  <div className="mt-auto pt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(f)}
                      data-testid={`i-facilities-edit-${f.id}`}
                      className="inline-flex items-center gap-1.5 text-[#234333] hover:bg-[#234333]/10 text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"
                    >
                      <Pencil size={13} strokeWidth={1.6} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(f)}
                      data-testid={`i-facilities-delete-${f.id}`}
                      className="inline-flex items-center gap-1.5 text-red-700 hover:bg-red-50 text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"
                    >
                      <Trash2 size={13} strokeWidth={1.6} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Facility" : "Add Facility"}</DialogTitle>
            <DialogDescription>
              Provide a clear name, a short description and up to {MAX_PHOTOS} photos (300 KB each).
            </DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Facility Name *" full>
              <input
                data-testid="i-facilities-input-name"
                className={inputCls}
                placeholder="e.g., Central Library"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                maxLength={160}
              />
            </Field>
            <Field label="Description" full>
              <textarea
                data-testid="i-facilities-input-description"
                className={`${inputCls} min-h-[110px] leading-relaxed`}
                placeholder="A short description of the facility — equipment, capacity, hours, etc."
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                maxLength={4000}
              />
            </Field>
            <Field label="Display Order">
              <input
                data-testid="i-facilities-input-order"
                type="number"
                className={inputCls}
                value={form.order}
                onChange={(e) => set("order", e.target.value)}
              />
            </Field>
            <Field label="Status">
              <select
                data-testid="i-facilities-input-status"
                className={inputCls}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Photos ({(form.photos || []).filter((p) => p?.url).length}/{MAX_PHOTOS}) · 300 KB each
                </div>
                {(form.photos || []).length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={addPhotoSlot}
                    data-testid="i-facilities-add-photo-slot"
                    className="inline-flex items-center gap-1.5 text-[#234333] hover:bg-[#234333]/10 text-xs font-semibold px-2.5 py-1 rounded-sm transition-colors"
                  >
                    <Plus size={12} strokeWidth={1.8} /> Add photo slot
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(form.photos || []).map((p, idx) => (
                  <div key={idx} className="relative border border-[#e5e0d8] rounded-sm p-2 bg-[#fbfaf7]">
                    <button
                      type="button"
                      onClick={() => removePhotoAt(idx)}
                      data-testid={`i-facilities-remove-photo-${idx}`}
                      className="absolute -top-2 -right-2 z-10 bg-white border border-[#e5e0d8] hover:bg-red-50 hover:border-red-300 text-red-600 rounded-full p-1 shadow-sm"
                      aria-label="Remove photo"
                    >
                      <XIcon size={12} strokeWidth={2} />
                    </button>
                    <FileUploader
                      kind="inst_facility_photo"
                      label={`Photo ${idx + 1}`}
                      url={p?.url || ""}
                      onChange={(next) => setPhotoAt(idx, next || {})}
                      testid={`i-facilities-photo-${idx}`}
                    />
                  </div>
                ))}
                {(form.photos || []).length === 0 && (
                  <div className="col-span-full text-sm text-[#6b655b] italic">
                    No photos added yet. Click <em>Add photo slot</em> to upload up to {MAX_PHOTOS} photos.
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-sm border border-[#e5e0d8] hover:bg-[#f5f1e8] transition-colors"
              data-testid="i-facilities-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold rounded-sm bg-[#234333] hover:bg-[#1a3326] text-white transition-colors disabled:opacity-60"
              data-testid="i-facilities-save-btn"
            >
              {saving ? "Saving…" : form.id ? "Update Facility" : "Add Facility"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this facility?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{confirmDelete?.name}</span> and all its photos will be removed permanently. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="i-facilities-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-red-600 hover:bg-red-700"
              data-testid="i-facilities-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
